/// <amd-module name="Environments/EnvironmentRunDetails/Components/JobsComponent" />
import React = require("react");

import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { JobsActionsCreator } from "Environments/EnvironmentRunDetails/ActionsCreators/JobsActionsCreator";
import { PageKey } from "Environments/EnvironmentRunDetails/Common/Utils";
import { JobsStore } from "Environments/EnvironmentRunDetails/Stores/JobsStore";
import * as Resources from "Environments/EnvironmentRunDetails/Resources";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { EnvironmentPhaseJobRecord } from "Environments/RemoteClients/Contracts/Traceability";
import { getJobStatusIndicatorData } from "Environments/RunsCommon/Indicator";
import { NavConstants, getBuildUrl } from "Environments/RunsCommon/Linking";

import { ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Duration } from "VSSUI/Duration";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from 'VSSUI/Spinner';
import { StatusSize, Status } from "VSSUI/Status"
import { Table, ITableColumn, SimpleTableCell, TwoLineTableCell, ITableRow } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { Area, FeatureNames, Action } from "Environments/EnvironmentRunDetails/Common/Utils";

interface IProps extends IEnvironmentBaseComponentProperties {
    instanceId: string;
    environmentId: number;
    environmentExecutionRecordId: number;
    ownerId: number;
}

interface IState extends IEnvironmentBaseComponentState {
    itemProvider: ArrayItemProvider<EnvironmentPhaseJobRecord>;
    showLoadingExperience: boolean;
}

class JobsComponent extends EnvironmentBaseComponent<IProps, IState> {

    public getInstanceId(context: IVssComponentContext): string {
        return "environment-jobs-intance-id-" + this.componentId;
    }

    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);

        this._store = StoreManager.CreateStore<JobsStore>(JobsStore, this._instanceId);
        this._actionCreator = ActionCreatorManager.CreateActionCreator<JobsActionsCreator>(JobsActionsCreator, this._instanceId);
        // syncResultFound is true when data is found in page data. showLoadingExperience can be set to false in that case.
        const syncResultFound = this._actionCreator.getJobs(context.pageContext, props.environmentId, props.environmentExecutionRecordId);
        publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentRunDetailsView, { "Action": Action.JobsTabNavigation, "EnvironmentId": props.environmentId.toString(), "EnvironmentExecutionRecordId": props.environmentExecutionRecordId.toString(), "OwnerId": props.ownerId.toString()});
        this.state = { itemProvider: this._getItemProvider(), errorMessages: {}, showLoadingExperience: !syncResultFound };
        this._store.addChangedListener(this._onStoreChange);
    }

    public render(): JSX.Element {
        if (this.state.showLoadingExperience && !this._messagesStore.getMessage(PageKey.EnvironmentDeploymentJobDetails)) {
            return (
                <div className="full-size flex-column justify-center">
                    <Spinner size={SpinnerSize.large} label={Resources.Loading} />
                </div>
            );
        }

        const itemProvider: ArrayItemProvider<EnvironmentPhaseJobRecord> = this.state.itemProvider;
        return (
            <div className="page-content page-content-top">
                <Card className="bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }} >
                    <div>
                        <Table
                            ariaLabel={Resources.JobColumnName}
                            key={"env-jobs-table"}
                            columns={this._sizableColumns}
                            itemProvider={itemProvider}
                            onActivate={this._onJobClick} />
                    </div>
                </Card>
            </div>
        );
    }

    private _onStoreChange = (): void => {
        this.setState({ itemProvider: this._getItemProvider(), showLoadingExperience: false });
    }

    private _getItemProvider(): ArrayItemProvider<EnvironmentPhaseJobRecord> {
        const jobs: EnvironmentPhaseJobRecord[] = this._store.getJobs();
        return new ArrayItemProvider<EnvironmentPhaseJobRecord>(jobs);
    }

    private _renderJobColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<EnvironmentPhaseJobRecord>, tableItem: EnvironmentPhaseJobRecord): JSX.Element => {
        const status = getJobStatusIndicatorData(tableItem.result);

        return (
            <SimpleTableCell 
                columnIndex={columnIndex} 
                tableColumn={tableColumn} 
                key={"Job-col-" + columnIndex} 
                contentClassName="fontWeightSemiBold fontSizeM scroll-hidden"
                ariaLabel={"Job-col-" + columnIndex}
            >
                <Tooltip text={status.label}>
                    <span className="flex-row">
                        <Status {...status.statusProps} className="icon-large-margin" size={StatusSize.l} ariaLabel={status.label}/>
                    </span>
                </Tooltip>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={tableItem.jobName}>
                        <span className="text-ellipsis">{tableItem.jobName}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        )
    }

    private _renderCompletedOnColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<EnvironmentPhaseJobRecord>, tableItem: EnvironmentPhaseJobRecord): JSX.Element => {
        const { startTime, finishTime } = tableItem

        const dateLine = (
            <div className="flex-row flex-center fontSize">
                <Icon iconName="Calendar" className="environments-initial-icon" />
                {startTime && <Ago
                    date={startTime}
                    format={AgoFormat.Compact}
                />}
            </div>
        );

        const durationLine = (
            <div className="flex-row flex-center fontSize bolt-table-two-line-cell-item">
                <Icon iconName="Clock" className="environments-initial-icon" />
                {startTime && finishTime && <Duration
                    startDate={startTime}
                    endDate={finishTime}
                />}
            </div>
        );

        return (
            <TwoLineTableCell
                key={"completedOn-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={dateLine}
                line2={durationLine}
            />
        );
    }

    private _onJobClick = (event: React.SyntheticEvent<HTMLElement, Event>, tableRow: ITableRow<EnvironmentPhaseJobRecord>) => {
        if (tableRow.data) {
            const pageContext = this.context.pageContext;
            let routes: { [key: string]: string } = {};
            routes[NavConstants.job] = tableRow.data.jobId;
            const url = getBuildUrl(pageContext, this.props.ownerId, routes);
            event.preventDefault();
            onClickFPS(pageContext, url, true, undefined);
        }
    }

    private _onSizeSizable = (event: MouseEvent, index: number, width: number): void => {
        (this._sizableColumns[index].width as ObservableValue<number>).value = width;
    }

    private _sizableColumns = [
        {
            id: "job-col",
            name: Resources.JobColumnName,
            minWidth: 200,
            width: new ObservableValue(514),
            className: "env-two-line-cell",
            renderCell: this._renderJobColumn
        },
        {
            id: "completedon-col",
            name: "",
            minWidth: 200,
            width: new ObservableValue(-100),
            className: "env-two-line-cell",
            renderCell: this._renderCompletedOnColumn,
            onSize: this._onSizeSizable
        }
    ];

    private _store: JobsStore;
    private _actionCreator: JobsActionsCreator;
}

VssComponent.register("jobsHistory", JobsComponent);
